import { useState } from "react";
import { AlertTriangle, Info, X } from "lucide-react";

interface SubscriptionBannerProps {
  status: string | null;
  cancelAtPeriodEnd: boolean;
}

const SubscriptionBanner = ({ status, cancelAtPeriodEnd }: SubscriptionBannerProps) => {
  const bannerKey = status === "past_due"
    ? "past_due"
    : status === "active" && cancelAtPeriodEnd
      ? "cancel_at_period_end"
      : null;
  const [dismissedKey, setDismissedKey] = useState<string | null>(null);

  if (!bannerKey || dismissedKey === bannerKey) return null;

  const dismissButton = (
    <button
      type="button"
      aria-label="Fechar aviso de assinatura"
      onClick={() => setDismissedKey(bannerKey)}
      className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current"
    >
      <X className="h-4 w-4" aria-hidden="true" />
    </button>
  );

  // Active + canceling at period end → info banner
  if (status === "active" && cancelAtPeriodEnd) {
    return (
      /* ⚠️ ISTO ESTAVA ILEGÍVEL, e é culpa da varredura que tirou as cores de
         status em 20/08: o azul virou `primary` em TUDO — chapa e texto — e
         sobrou `bg-primary` com `text-primary`, Brasa sobre Brasa. O aviso de
         que a assinatura vai ser cancelada era um retângulo laranja em branco.

         Agora é cartão de hairline, como todo cartão do app: o assunto é
         informativo ("você continua com acesso até lá"), não merece o acento
         nem uma chapa. Quem carrega o tom é o ícone. */
      <div
        data-testid="subscription-banner-safe-area"
        className="subscription-banner sticky top-[max(0.75rem,env(safe-area-inset-top))] z-40 mx-3 flex items-start gap-2.5 rounded-[--radius] border border-border bg-card p-3 pr-10"
        style={{ marginTop: "max(0.75rem, env(safe-area-inset-top))" }}
      >
        <Info size={16} strokeWidth={1.6} className="text-muted-foreground mt-0.5 flex-shrink-0" />
        <p className="text-xs text-foreground">
          Sua assinatura será cancelada no fim do período atual. Você continua com acesso até lá.
        </p>
        {dismissButton}
      </div>
    );
  }

  // Past due → warning banner
  if (status === "past_due") {
    return (
      /* ⚠️ INVERSÃO, e é a regra do design system, não invenção: o sistema NÃO
         TEM cor de erro — "crítico = inversão". Pagamento falhando é a mensagem
         mais séria que este app dá, e antes da varredura ela era âmbar; depois
         dela virou cinza sobre cinza, indistinguível de um aviso qualquer.

         Chapa de tinta com texto no papel resolve os dois problemas de uma vez:
         separa do resto da tela sem gastar o acento, e não depende de a pessoa
         distinguir matiz. */
      <div
        data-testid="subscription-banner-safe-area"
        className="subscription-banner sticky top-[max(0.75rem,env(safe-area-inset-top))] z-40 mx-3 flex items-start gap-2.5 rounded-[--radius] bg-foreground p-3 pr-10 text-background"
        style={{ marginTop: "max(0.75rem, env(safe-area-inset-top))" }}
      >
        <AlertTriangle size={16} strokeWidth={1.6} className="mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-xs font-semibold">
            Problema com seu pagamento
          </p>
          <p className="text-xs mt-0.5 opacity-90">
            Estamos tentando processar novamente. Se o problema persistir, entre em contato pelo WhatsApp.
          </p>
        </div>
        {dismissButton}
      </div>
    );
  }

  // null, "active", or any other status → no banner
  return null;
};

export default SubscriptionBanner;
